import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import BookingForm from "@/components/BookingForm";
import { BookingRow } from "@/components/BookingRow";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FILTERS = ["all", "confirmed", "checked_in", "pending", "checked_out", "cancelled"];

export default function Bookings() {
  const [bookings, setBookings] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [filter, setFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const { fmt } = useCurrency();
  const navigate = useNavigate();

  const load = () => {
    api.get("/bookings").then(({ data }) => setBookings(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const action = async (id, act, label) => {
    try {
      await api.post(`/bookings/${id}/${act}`);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendKey = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/send-key`);
      toast.success(`Digital key ${data.digital_key} sent to guest via SMS (EasyStay)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createInvoice = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/invoice`);
      toast.success(`Invoice ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const visible = (bookings || []).filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Bookings</h1>
          <p className="text-sm text-muted-foreground">Reservations, check-ins and check-outs.</p>
        </div>
        <Button onClick={() => setFormOpen(true)} data-testid="new-booking-button" className="transition-colors">
          <Plus size={16} className="mr-2" /> New booking
        </Button>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="booking-filter-tabs">
          {FILTERS.map((f) => (
            <TabsTrigger key={f} value={f} data-testid={`filter-${f}`} className="capitalize">
              {f === "all" ? "All" : f.replace("_", " ")}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3 font-medium">Guest</th>
              <th className="p-3 font-medium">Room</th>
              <th className="p-3 font-medium">Dates</th>
              <th className="p-3 font-medium">Nights</th>
              <th className="p-3 text-right font-medium">Total</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Channel</th>
              <th className="p-3 font-medium">Sync</th>
              <th className="p-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(visible || []).map((b) => (
              <BookingRow key={b.id} b={b} fmt={fmt} onAction={action} onInvoice={createInvoice} onSendKey={sendKey} />
            ))}
          </tbody>
        </table>
        {bookings && visible.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="bookings-empty">No bookings in this view.</div>
        )}
      </div>

      <BookingForm open={formOpen} onOpenChange={setFormOpen} rooms={rooms} onSaved={load} />
    </div>
  );
}
